import {
  isValidCepLength,
  isValidCpfLength,
  isValidPhoneFormat,
  normalizeRepresentativePhoneInput,
} from '../services/utils/stringUtils';
import {
  DELIVERY_ADDRESS_STREET_NUMBER_EMPTY_ERROR,
  DELIVERY_ADDRESS_STREET_NUMBER_INVALID_ERROR,
  isFilledDeliveryAddressStreetNumber,
  isValidDeliveryAddressStreetNumber,
} from './deliveryAddressStreetNumber';
import {
  DELIVERY_ADDRESS_STREET_NAME_EMPTY_ERROR,
  DELIVERY_ADDRESS_STREET_NAME_INVALID_ERROR,
  isValidDeliveryAddressStreetName,
} from './deliveryAddressStreetName';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type AdminInlineFieldErrors = Partial<
  Record<'nomeCompleto' | 'cpf' | 'telefone1', string>
>;

export type RepInlineFieldErrors = Partial<
  Record<'nome' | 'cpf' | 'email' | 'telefone', string>
>;

export type DeliveryAddressInlineFieldErrors = Partial<
  Record<'cep' | 'estado' | 'cidade' | 'endereco' | 'numero' | 'bairro', string>
>;

export type ReceiverInlineFieldErrors = Partial<Record<'nome' | 'cpf', string>>;

export function hasInlineFieldErrors<T extends object>(errors: T): boolean {
  return Object.values(errors).some((value) => typeof value === 'string' && value.length > 0);
}

export function validateAdminInlineEdit(data: {
  nomeCompleto: string;
  cpf: string;
  telefone1: string;
}): AdminInlineFieldErrors {
  const errors: AdminInlineFieldErrors = {};

  if (!data.nomeCompleto.trim()) {
    errors.nomeCompleto = 'Informe o nome completo do responsável.';
  }

  if (!data.cpf.trim() || !isValidCpfLength(data.cpf)) {
    errors.cpf = 'CPF deve ter 11 dígitos.';
  }

  if (!data.telefone1.trim()) {
    errors.telefone1 = 'Informe o telefone principal.';
  } else if (!isValidPhoneFormat(data.telefone1)) {
    errors.telefone1 = 'Telefone deve ter 10 dígitos (fixo) ou 11 dígitos (celular).';
  }

  return errors;
}

export function validateRepInlineEdit(data: {
  nome: string;
  cpf: string;
  email: string;
  telefone: string;
}): RepInlineFieldErrors {
  const errors: RepInlineFieldErrors = {};

  if (!data.nome.trim()) {
    errors.nome = 'Informe o nome do representante.';
  }

  if (!data.cpf.trim() || !isValidCpfLength(data.cpf)) {
    errors.cpf = 'CPF deve ter 11 dígitos.';
  }

  const emailTrim = (data.email || '').trim();
  if (!emailTrim || !EMAIL_REGEX.test(emailTrim)) {
    errors.email = 'Informe um e-mail válido (proposta comercial).';
  }

  const telNorm = normalizeRepresentativePhoneInput(data.telefone || '');
  if (telNorm.length !== 10 && telNorm.length !== 11) {
    errors.telefone = 'Telefone deve ter 10 ou 11 dígitos numéricos.';
  }

  return errors;
}

export function validateReceiverInlineEdit(data: {
  nome: string;
  cpf: string;
}): ReceiverInlineFieldErrors {
  const errors: ReceiverInlineFieldErrors = {};

  if (!data.nome.trim()) {
    errors.nome = 'Informe o nome do recebedor.';
  }

  if (!data.cpf.trim() || !isValidCpfLength(data.cpf)) {
    errors.cpf = 'CPF deve ter 11 dígitos.';
  }

  return errors;
}

export function validateDeliveryAddressInlineEdit(data: {
  cep: string;
  estado: string;
  cidade: string;
  endereco: string;
  numero: string;
  bairro: string;
}): DeliveryAddressInlineFieldErrors {
  const errors: DeliveryAddressInlineFieldErrors = {};

  if (!isValidCepLength(data.cep)) {
    errors.cep = 'CEP inválido (8 dígitos).';
  }

  const uf = (data.estado || '').trim().toUpperCase();
  if (uf.length !== 2) {
    errors.estado = 'UF deve ter 2 letras.';
  }

  if (!data.cidade.trim()) {
    errors.cidade = 'Informe a cidade.';
  }

  if (!isValidDeliveryAddressStreetName(data.endereco)) {
    errors.endereco = data.endereco.trim()
      ? DELIVERY_ADDRESS_STREET_NAME_INVALID_ERROR
      : DELIVERY_ADDRESS_STREET_NAME_EMPTY_ERROR;
  }

  if (!isFilledDeliveryAddressStreetNumber(data.numero)) {
    errors.numero = DELIVERY_ADDRESS_STREET_NUMBER_EMPTY_ERROR;
  } else if (!isValidDeliveryAddressStreetNumber(data.numero)) {
    errors.numero = DELIVERY_ADDRESS_STREET_NUMBER_INVALID_ERROR;
  }

  if (!data.bairro.trim()) {
    errors.bairro = 'Informe o bairro.';
  }

  return errors;
}
